import React, { FC } from 'react'
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@common/http/query-client';
import { Skeleton } from '@ui/skeleton/skeleton';

import { HomeResponse } from '@player/types';
import { AlbumImage } from '@app/web-player/albums/album-image/album-image';

import { AlbumLink } from '@app/web-player/albums/album-link';
import { ArtistLinks } from '@app/web-player/artists/artist-links';

import { queueGroupId } from '@app/web-player/queue-group-id';
import { PlaybackToggleButton } from '@app/web-player/playable-item/playback-toggle-button';

import { LikeButton } from '@player/custom/LikeButton'
import { IconButton } from '@ui/buttons/icon-button';

import { DialogTrigger } from '@ui/overlays/dialog/dialog-trigger';
import { AlbumContextDialog } from '@app/web-player/albums/album-context-dialog';

import { MoreHorizIcon } from '@ui/icons/material/MoreHoriz';
import { ContentGrid } from '@app/web-player/playable-item/content-grid';

import { ArtistGridItem } from '@app/web-player/artists/artist-grid-item';
import { AlbumGridItem } from '@app/web-player/albums/album-grid-item';

import { Button } from '@ui/buttons/button';
import { Link } from 'react-router';

import { Trans } from '@ui/i18n/trans';
import { TrackTable } from '@player/web-player/tracks/track-table/track-table';

import { IllustratedMessage } from '@ui/images/illustrated-message';
import { LargeArtistImage } from '@player/web-player/artists/artist-image/large-artist-image';

import { LikeButton as LikeButton2 } from '@app/web-player/library/like-button';
import { GenreLink } from '@app/web-player/genres/genre-link';

import { getBootstrapData } from '@ui/bootstrap-data/bootstrap-data-store';
import { Image } from '@player/custom/Image';

export const Home: FC = () => {
    const { data, isLoading } = useQuery<HomeResponse>({
        queryKey: ['homepage'],
        queryFn: async () => {

            return apiClient.get('home').then((res) => res.data)
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        retry: false,
        placeholderData: () => {
            const data = getBootstrapData().loaders?.landingPage as unknown as HomeResponse

            return data ? data : undefined
        }
    })

    return (
        <>
            <div
                className="bg-hover/5 relative justify-center flex flex-col py-250 bg-cover bg-top bg-no-repeat mb-8"
                style={{
                    // backgroundImage: data?.featured ? `url(${data?.featured?.image})` : undefined,
                    minHeight: 700
                }}
            >
                {data?.featured && data?.featured?.image && (
                    <Image
                        data={{
                            src: data?.featured?.image,
                            alt: data?.featured?.name || 'Featured Album',
                            width: 800,  // Reduce from 1920
                            height: 450, // Reduce from 1080
                            // Add these for better performance
                            srcSet: `${data?.featured?.image}?w=400 400w, ${data?.featured?.image}?w=800 800w, ${data?.featured?.image}?w=1200 1200w`,
                            sizes: "(max-width: 768px) 100vw, 800px"
                        }}
                        fadeInDuration={0}
                        priority
                        usePlaceholder={false}  // Disable placeholder for LCP
                        className="!absolute inset-0 object-cover w-full h-full z-10"
                        layout="fill"  // Use fill layout for absolute positioning
                        objectFit="cover"
                        imgClassName="object-top"
                    />
                )}

                <span className="bg-background/50 absolute z-20 inset-0" />

                <div className="@container p-16 mx-auto w-full relative z-50 md:p-30 web-player-container">
                    <h1 className="text-4xl md:text-6xl mb-30 relative z-20 font-black">
                        New tracks of<br />
                        {data?.featured ? <strong className="text-primary">{data?.featured?.name}</strong> : <Skeleton variant="rect" className="md:!w-200 !h-30 md:!h-40 !inline-block" />} Out now!
                    </h1>

                    <div className="w-fit flex items-center gap-4">
                        <div className="bg-hover/10 backdrop-blur-sm h-52 flex items-center gap-10 p-4 rounded-l">
                            {!data?.featured ? (
                                <>
                                    <Skeleton variant="rect" className="!w-40 !h-40" />
                                    <Skeleton variant="rect" className="!w-40 !h-40" />

                                    <Skeleton variant="rect" className="!w-40 !h-40" />

                                    <div className="w-160">
                                        <Skeleton variant="text" />
                                        <Skeleton variant="text" />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <PlaybackToggleButton
                                        disabled={!data?.featured.tracks?.length}
                                        buttonType="icon"
                                        queueId={queueGroupId(data?.featured)}
                                        className="[&>div]:!w-24 [&>div]:!h-24 [&>div>img]:!w-24 [&>div>img]:!h-24 [&>div>img]:object-cover [&>svg]:!w-42 [&>svg]:!h-42"
                                        equalizerColor="white"
                                        radius="rounded"
                                    />

                                    <LikeButton
                                        item={data?.featured}
                                    />

                                    <AlbumImage album={data?.featured} className="w-42 h-42 rounded object-top" />
                                    <div className="w-160">
                                        <AlbumLink className="block max-w-160 truncate" album={data.featured} />
                                        <ArtistLinks className="block text-sm text-muted w-160 truncate" artists={data?.featured?.artists} />
                                    </div>
                                </>
                            )}
                        </div>

                        {data?.featured ? (
                            <DialogTrigger type="popover" mobileType="tray">
                                <IconButton
                                    className="w-52 h-52 z-20 !bg-hover/10 !bg-opacity-100 backdrop-blur-sm rounded-r p-4"
                                >
                                    <MoreHorizIcon className="w-32 h-32" />
                                </IconButton>

                                <AlbumContextDialog album={data?.featured} />
                            </DialogTrigger>
                        ) : (
                            <Skeleton variant="rect" className="!w-52 !h-52 rounded-r p-4" />
                        )}
                    </div>
                </div>
            </div>

            <div className="p-16 z-50 w-full relative mx-auto @container md:p-30 web-player-container -mt-[150px]">
                <div className="flex items-center gap-30 mb-20">
                    <h2 className="text-2xl font-bold"><Trans message="New releases"/></h2>

                    <Button className="ml-auto">
                        <Link to="/latest-releases" viewTransition>
                            <Trans message="View all" />
                        </Link>
                    </Button>
                </div>

                <ContentGrid>
                    {!isLoading && data?.latestReleases && data?.latestReleases?.length > 0 ? data?.latestReleases?.map((album) => (
                        <AlbumGridItem
                            album={album}
                            key={album.id}
                        />
                    )) : Array?.from({ length: 8 })?.map((_, index) => (
                        <Skeleton
                            key={`albumLoading_${index}`}
                            variant="rect"
                            className="!aspect-square !h-full !w-full"
                        />
                    ))}
                </ContentGrid>
            </div>

            <div className="bg-hover my-30 w-full">
                <div className="z-50  w-full relative mx-auto @container web-player-container grid grid-cols-1 md:grid-cols-2 md:gap-50">
                    <div className="border-b flex flex-col md:border-b-0 md:border-r p-16 md:p-30">
                        <div className="flex items-center gap-30 mb-20">
                            <h2 className="text-2xl font-bold">
                                <small className="block uppercase text-sm text-muted"><Trans message="Top"/></small>
                                <Trans message="10 of the week"/>
                            </h2>
                        </div>

                        {isLoading ? (
                            <>
                                loadin g....
                            </>
                        ) : data?.topTracksOfTheWeek && data?.topTracksOfTheWeek?.length > 0 ? (
                            <TrackTable
                                tracks={data?.topTracksOfTheWeek}
                                hideHeaderRow
                                hideArtist
                                hideAddedAtColumn
                                hideAlbum
                                hideGenres
                                hidePlaylistButton
                                hideQueueButton
                            />
                        ) : (
                            <IllustratedMessage
                                title={<Trans message="Nothing to display" />}
                                description={
                                    <Trans message="No trak found for top 10 of the week" />
                                }
                                className="my-auto"
                            />
                        )}
                    </div>

                    <div className="p-16 flex flex-col  md:p-30 w-full">
                        <div className="flex items-center gap-30 mb-20">
                            <h2 className="text-2xl font-bold">
                                <small className="block uppercase text-sm text-muted"><Trans message="Top"/></small>
                                <Trans message="10 of the month"/>
                            </h2>
                        </div>

                        {isLoading ? (
                            <>
                                loading ...
                            </>
                        ) : data?.topTracks && data?.topTracks?.length > 0 ? (
                            <TrackTable
                                tracks={data?.topTracks}
                                hideHeaderRow
                                hideArtist
                                hideAddedAtColumn
                                hideAlbum
                                hideGenres
                                hidePlaylistButton
                                hideQueueButton
                            />
                        ) : (
                            <IllustratedMessage
                                title={<Trans message="Nothing to display" />}
                                description={
                                    <Trans message="No trak found for top 10 of the month" />
                                }
                                className="my-auto"
                            />
                        )}
                    </div>
                </div>
            </div>

            <div className="p-16 z-50 w-full relative mx-auto @container md:p-30 web-player-container">

                <div className="flex items-center gap-30 mb-20">
                    <h2 className="text-2xl font-bold"><Trans message="Popular artists you may like"/></h2>

                    <Button className="ml-auto">
                        <Link to="/popular-artist" viewTransition>
                            <Trans message="View all" />
                        </Link>
                    </Button>
                </div>

                <ContentGrid>
                    {!isLoading && data?.popularArtists && data?.popularArtists?.length > 0 ? data?.popularArtists?.map((artist) => (
                        <ArtistGridItem
                            artist={artist}
                            key={artist.id}
                        />
                    )) : (
                        <>
                            loading new artist...
                        </>
                    )}
                </ContentGrid>
            </div>

            <div className="bg-hover my-120 py-50">
                <div className="p-16 z-50 w-full grid grid-cols-1 md:grid-cols-2 gap-100 relative mx-auto @container md:p-30 web-player-container">
                    <div>
                        <h1 className="text-4xl md:text-6xl text-accent mb-20 relative z-20 font-black">
                            Artist<br />
                            of the week<br />
                            {data?.artistOfTheWeek ? <strong className="text-primary">{data?.artistOfTheWeek?.name}</strong> : <Skeleton variant="rect" className="!w-200 !h-40 !inline-block" />}
                        </h1>
                        {data?.artistOfTheWeek ? (
                            <>
                                <ul className="flex mb-30 items-center justify-start gap-10 leading-snug flex-wrap text-sm text-muted max-md:hidden">
                                    {data?.artistOfTheWeek?.genres?.slice(0, 10).map(genre => (
                                        <li key={genre.id}>
                                            <GenreLink genre={genre} />
                                        </li>
                                    ))}
                                </ul>

                                <div className="flex items-center gap-10">
                                    <LikeButton2
                                        likeable={data?.artistOfTheWeek}
                                        //color="white"
                                        variant="raised"
                                    />
                                    <Button
                                        variant="outline"
                                        radius="rounded-full"
                                        //color="white"
                                    >
                                        <Link to={`/artist/${data?.artistOfTheWeek?.id}`} viewTransition>
                                            <Trans message="View artist" />
                                        </Link>
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center gap-10">
                                <Skeleton variant="rect" className="!w-100 !h-40 !inline-block" />
                            </div>
                        )}
                    </div>
                    <div className="relative">
                        {data?.artistOfTheWeek && (
                            <LargeArtistImage
                                artist={data?.artistOfTheWeek}
                                className="w-full h-full object-cover rounded md:absolute inset-0"
                                wrapperClassName="md:!absolute bg-alt dark:bg-background w-full md:-bottom-140 md:-top-140 left-0 right-0"
                            />
                        )}
                    </div>
                </div>
            </div>

            <div className="p-16 mb-30 z-50 w-full relative mx-auto @container md:p-30 web-player-container">
                <div className="flex items-center gap-30 mb-20">
                    <h2 className="text-2xl font-bold"><Trans message="Popular releases"/></h2>

                    <Button className="ml-auto">
                        <Link to="/popular-releases" viewTransition>
                            <Trans message="View all" />
                        </Link>
                    </Button>
                </div>

                <ContentGrid>
                    {!isLoading && data?.popularReleases && data?.popularReleases?.length > 0 ? data?.popularReleases?.map((album) => (
                        <AlbumGridItem
                            album={album}
                            key={album.id}
                        />
                    )) : (
                        <>
                            loading new releases...
                        </>
                    )}
                </ContentGrid>
            </div>
        </>
    )
}
